import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './theme/theme';
import MainLayout from './components/Layout';
import DashboardLayout from './components/DashboardLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import des écrans
import HomeScreen from './screens/HomeScreen';
import SearchScreen from './screens/SearchScreen';
import AddDressScreen from './screens/AddDressScreen';
import ProfileScreen from './screens/ProfileScreen';
import PublicProfileScreen from './screens/PublicProfileScreen';
import DressDetailScreen from './screens/DressDetailScreen';
import LoginScreen from './screens/auth/LoginScreen';
import RegisterScreen from './screens/auth/RegisterScreen';
import PaymentSuccessScreen from './screens/PaymentSuccessScreen';
import PaymentCancelScreen from './screens/PaymentCancelScreen';
import StripeConnectReturn from './screens/StripeConnectReturn';
import StripeConnectRefresh from './screens/StripeConnectRefresh';
import ActivationPaiementSuccess from './screens/ActivationPaiementSuccess';
import ActivationPaiement from './screens/ActivationPaiement';
import RechercheTab from './screens/dashboard/RechercheTab';
import ProfileTab from './screens/dashboard/ProfileTab';
import ClosetTab from './screens/dashboard/ClosetTab';
import LikedTab from './screens/dashboard/LikedTab';
import HistoryTab from './screens/dashboard/HistoryTab';
import ChatTab from './screens/dashboard/ChatTab';
import SettingsTab from './screens/dashboard/SettingsTab';
import AdminScreen from './screens/AdminScreen';
import AdminAccess from './screens/AdminAccess';
import { isUserAdmin } from './utils/adminUtils';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const [checkingAdmin, setCheckingAdmin] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);

  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (loading) return;
      
      if (!user) {
        setCheckingAdmin(false);
        setIsAdmin(false);
        return;
      }

      try {
        const adminStatus = await isUserAdmin(user.uid, user.email || undefined);
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Erreur lors de la vérification admin:', error);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, loading]);

  if (loading || checkingAdmin) {
    return null; // Ou un loader
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    // Rediriger vers le dashboard avec un message d'erreur
    return <Navigate to="/dashboard" replace state={{ error: 'Accès non autorisé' }} />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<MainLayout />}>
              <Route index element={<HomeScreen />} />
              <Route path="search" element={<SearchScreen />} />
              <Route path="add-dress" element={
                <RequireAuth>
                  <AddDressScreen />
                </RequireAuth>
              } />
              <Route path="profile" element={
                <RequireAuth>
                  <ProfileScreen />
                </RequireAuth>
              } />
              <Route path="user/:userId" element={<PublicProfileScreen />} />
              <Route path="dress/:id" element={<DressDetailScreen />} />
              <Route path="login" element={<LoginScreen />} />
              <Route path="register" element={<RegisterScreen />} />
              <Route path="success" element={<PaymentSuccessScreen />} />
              <Route path="cancel" element={<PaymentCancelScreen />} />
              <Route path="connect/return" element={<StripeConnectReturn />} />
              <Route path="connect/refresh" element={<StripeConnectRefresh />} />
              <Route path="activation-paiement-success" element={<ActivationPaiementSuccess />} />
              <Route path="activation-paiement" element={<ActivationPaiement />} />
            </Route>

            {/* Routes du dashboard */}
            <Route path="/dashboard/*" element={
              <RequireAuth>
                <DashboardLayout />
              </RequireAuth>
            }>
              {/* Redirection automatique de /dashboard vers /dashboard/profile */}
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="recherche" element={<RechercheTab />} />
              <Route path="profile" element={<ProfileTab />} />
              <Route path="closet" element={<ClosetTab />} />
              <Route path="liked" element={<LikedTab />} />
              <Route path="history" element={<HistoryTab />} />
              <Route path="chat" element={<ChatTab />} />
              <Route path="settings" element={<SettingsTab />} />
              <Route path="dress/:id" element={<DressDetailScreen />} />
            </Route>

            {/* Route d'administration */}
            <Route path="/admin" element={<AdminAccess />} />
            <Route path="/admin/dashboard/*" element={
              <RequireAdmin>
                <AdminScreen />
              </RequireAdmin>
            } />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
